import { useApolloClient } from "@apollo/client";
import { Box, Icon, Tooltip, useDisclosure, VStack } from "@chakra-ui/react";
import React, { useCallback, useMemo, useState } from "react";
import { RiEyeOffLine } from "react-icons/ri";
import { useVerificationModalFiles } from "src/components/Form/hooks/useVerificationModalFiles";
import { AdminQuestionAnswer } from "src/components/Form/QuestionAnswer";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { useVisibilityContext } from "src/components/Providers/FormProvider";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import {
  DocumentStorageStatus,
  useDocumentStorage,
} from "src/hooks/useDocumentStorage";
import { useFormAnswerUpsert } from "src/hooks/useFormAnswerUpsert";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import { UPSERT_VERIFICATION_STATUS } from "src/scenes/orgAdmin/forms/graphql/mutations";
import {
  GET_FORM_VIEW_BY_ID,
  GET_VERIFICATIONS_FOR_ADMIN,
} from "src/scenes/orgAdmin/forms/graphql/queries";
import { formatNumberStringToNumber } from "src/services/format";
import { Answer, Question } from "src/services/formTemplate";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import { VerificationModal } from "./VerificationModal";
import { VerificationSection } from "./VerificationSection";

type Props = {
  form: GQL.GetFormViewById_form_by_pk;
  questions: AF.Question<AF.WithId>[];
  form_answers: GQL.FormAnswerFragment[];
  grades_answers: GQL.GradesAnswersFragment[];
  address_answers: GQL.AddressAnswersFragment[];
  custom_question_answers: GQL.CustomQuestionAnswersFragment[];
  custom_question_answer_bank_relationships?: GQL.CustomQuestionAnswerBankRelationshipsFragment[];
  rankedSchoolIds: uuid[];
  previousFormSchoolIds: uuid[];
  canEditVerificationData: boolean;
  showHiddenQuestions?: boolean;
};

type VerificationModalFile = {
  id: string;
  name: string;
  url: string;
  identityAiEligible: boolean;
};

export const QuestionList: React.FC<Props> = ({
  form,
  questions,
  form_answers,
  grades_answers,
  address_answers,
  custom_question_answers,
  custom_question_answer_bank_relationships,
  rankedSchoolIds,
  previousFormSchoolIds,
  canEditVerificationData,
  showHiddenQuestions = false,
}) => {
  const isParentUser = useRequiredHasuraRoles([HasuraRole.USER]);
  const visibilityContext = useVisibilityContext();
  const verificationModalFiles = useVerificationModalFiles({
    rankedSchoolIds,
    previousFormSchoolIds,
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const flags = useFlags(["verification-modal"]);
  const isVerificationModalEnabled = flags["verification-modal"]?.enabled;
  const [isSubmittingVerification, setIsSubmittingVerification] =
    useState(false);

  // State for the selected verification and dropdown
  const [selectedVerification, setSelectedVerification] = useState<{
    verification: AF.FormVerification<AF.WithId>;
    questions: AF.Question<AF.WithId>[];
    answers: Record<string, any>;
    files: VerificationModalFile[];
  } | null>(null);

  // Track the current verification status in the modal
  const [currentVerificationStatus, setCurrentVerificationStatus] =
    useState<GQL.verification_status_enum | null>(null);

  // Which document to show first when the modal opens
  const [initialFileIndex, setInitialFileIndex] = useState(0);

  // For accessing document storage functions
  const { getDownloadUrl, getDocumentInfo } = useDocumentStorage();
  const avelaToast = useAvelaToast();

  const answers = useMemo(
    () =>
      Answer.getFormikInitialValues(
        questions,
        form_answers,
        grades_answers,
        address_answers,
        custom_question_answers,
        custom_question_answer_bank_relationships
      ),
    [
      questions,
      form_answers,
      grades_answers,
      address_answers,
      custom_question_answers,
      custom_question_answer_bank_relationships,
    ]
  );

  const completeQuestions = useMemo(
    () => Question.getCompleteQuestions(questions),
    [questions]
  );
  const completeApplicableQuestions = useMemo(
    () =>
      Question.getCompleteApplicableQuestions(
        questions,
        answers,
        {
          rankedSchoolIds,
          previousFormSchoolIds,
        },
        visibilityContext
      ),
    [
      answers,
      previousFormSchoolIds,
      questions,
      rankedSchoolIds,
      visibilityContext,
    ]
  );
  const applicableQuestionIdSet = useMemo(() => {
    return new Set(completeApplicableQuestions.map((q) => q.id));
  }, [completeApplicableQuestions]);

  const questionsGroupedByVerifications = useMemo(
    () =>
      Question.groupByVerifications(
        showHiddenQuestions ? completeQuestions : completeApplicableQuestions
      ),
    [showHiddenQuestions, completeQuestions, completeApplicableQuestions]
  );

  // Pre-calculate which verifications have file uploads
  const verificationsWithUploads = useMemo(() => {
    const result: Record<string, boolean> = {};

    // Process each verification group in completeQuestions
    questionsGroupedByVerifications.forEach((group) => {
      // Skip questions without verification
      if (!group.verification) return;

      const verificationId = group.verification.id;

      // Find file upload questions
      const fileUploadQuestions = group.questions.filter(
        (q: AF.Question<AF.WithId>) => q.type === AF.FileUploadType
      );

      // Check if any file upload questions have answers
      let hasUploads = false;
      for (const question of fileUploadQuestions) {
        const answer = answers[question.id];
        if (Array.isArray(answer) && answer.length > 0) {
          hasUploads = true;
          break;
        }
      }

      result[verificationId] = hasUploads;
    });

    return result;
  }, [questionsGroupedByVerifications, answers]);

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "Reset verification?",
    body: "Resetting this status will set the verification back to a pending state.",
    cancelButton: {
      label: "No, cancel",
    },
    confirmButton: {
      label: "Yes, reset",
      colorScheme: "red",
    },
    translate: true,
  });

  const [upsertVerification] = useRemoteDataMutation<
    GQL.UpsertVerificationStatus,
    GQL.UpsertVerificationStatusVariables
  >(UPSERT_VERIFICATION_STATUS);

  // Get the form answer upsert hook for updating answers
  const { upsertFormAnswers } = useFormAnswerUpsert();

  // Get the refetch function from Apollo Client to manually refresh data
  const client = useApolloClient();

  // Handle verification status change
  const verificationHandler = useCallback(
    async (
      formId: uuid,
      verificationId: uuid,
      verificationLabel: string,
      status: GQL.verification_status_enum
    ) => {
      try {
        setIsSubmittingVerification(true);
        // Confirmation before setting status to Pending
        if (
          status === GQL.verification_status_enum.Pending &&
          !(await confirm())
        ) {
          return;
        }

        // Update the database with the new verification status
        await upsertVerification({
          variables: {
            form_id: formId,
            form_verification_id: verificationId,
            verification_status: status,
          },
        });

        // Refetch the form view and verifications queries to immediately update the UI
        await client.refetchQueries({
          include: [GET_FORM_VIEW_BY_ID, GET_VERIFICATIONS_FOR_ADMIN],
        });
      } catch (error: unknown) {
        console.error(error);
        avelaToast.error({
          title: `Error updating ${verificationLabel} verification`,
        });
      } finally {
        setIsSubmittingVerification(false);
      }
    },
    [confirm, upsertVerification, client, avelaToast]
  );

  // Handle answer update
  const handleAnswerUpdate = useCallback(
    async (questionId: string, value: any, skipAutoVerification = false) => {
      if (!selectedVerification || !form.id) {
        return;
      }

      // Update the local state immediately for responsive UI
      const updatedAnswers = {
        ...selectedVerification.answers,
        [questionId]: value,
      };

      setSelectedVerification({
        ...selectedVerification,
        answers: updatedAnswers,
      });

      // Prepare the variables for the mutation based on question type
      const question = selectedVerification.questions.find(
        (q) => q.id === questionId
      );

      if (!question) {
        return;
      }

      // Handle Number fields via upsertFormAnswers (supports form-service flag)
      if (question.type === "Number") {
        const numberValue = formatNumberStringToNumber(value);

        upsertFormAnswers(
          {
            form_id: form.id,
            answers: [
              {
                form_id: form.id,
                question_id: questionId,
                number_answer: numberValue,
              },
            ],
            single_or_multi_select_question_ids: [],
            grades_answers: [],
            address_answers: [],
            address_answer_deletes: [],
            custom_question_ids: [],
            custom_question_answers: [],
            answer_bank_ids: [],
            answer_bank_answers: [],
            deleted_answers_custom_question_ids: [],
            new_answer_bank_entries: [],
          },
          undefined,
          {
            awaitRefetchQueries: true,
          }
        )
          .then(async () => {
            await client.refetchQueries({
              include: [GET_FORM_VIEW_BY_ID, GET_VERIFICATIONS_FOR_ADMIN],
            });

            if (
              !skipAutoVerification &&
              selectedVerification?.verification?.id
            ) {
              await verificationHandler(
                form.id,
                selectedVerification.verification.id,
                selectedVerification.verification.label,
                GQL.verification_status_enum.Verified
              );
            }
          })
          .catch((error) => {
            console.error("Failed to update number answer:", error);
            avelaToast.error({
              title: "Something went wrong!",
              description: "Check your network and try again",
            });
          });

        return;
      }

      // Handle SingleSelect and MultiSelect fields via upsertFormAnswers (supports form-service flag)
      if (question.type === "SingleSelect" || question.type === "MultiSelect") {
        const options =
          question.type === "SingleSelect"
            ? !value
              ? []
              : [{ form_question_option_id: value }]
            : Array.isArray(value)
            ? value.map((optionId: string) => ({
                form_question_option_id: optionId,
              }))
            : [];

        upsertFormAnswers(
          {
            form_id: form.id,
            answers: [
              {
                form_id: form.id,
                question_id: questionId,
                form_answer_options: { data: options },
              },
            ],
            single_or_multi_select_question_ids: [questionId],
            grades_answers: [],
            address_answers: [],
            address_answer_deletes: [],
            custom_question_ids: [],
            custom_question_answers: [],
            answer_bank_ids: [],
            answer_bank_answers: [],
            deleted_answers_custom_question_ids: [],
            new_answer_bank_entries: [],
          },
          undefined,
          {
            awaitRefetchQueries: true,
          }
        )
          .then(async () => {
            await client.refetchQueries({
              include: [GET_FORM_VIEW_BY_ID, GET_VERIFICATIONS_FOR_ADMIN],
            });

            if (
              !skipAutoVerification &&
              selectedVerification?.verification?.id
            ) {
              await verificationHandler(
                form.id,
                selectedVerification.verification.id,
                selectedVerification.verification.label,
                GQL.verification_status_enum.Verified
              );
            }
          })
          .catch((error) => {
            console.error("Failed to update select answer:", error);
            avelaToast.error({
              title: "Something went wrong!",
              description: "Check your network and try again",
            });
          });

        return;
      }

      // Handle Address fields separately
      if (question.type === "Address") {
        // Use the hook to update the address answer in the database
        upsertFormAnswers(
          {
            form_id: form.id,
            single_or_multi_select_question_ids: [],
            answers: [],
            grades_answers: [],
            address_answers: [
              {
                form_id: form.id,
                question_id: questionId,
                street_address: value?.street_address || "",
                street_address_line_2: value?.street_address_line_2 || "",
                city: value?.city || "",
                state: value?.state || "",
                zip_code: value?.zip_code || "",
                geom: value?.geom || null,
              },
            ],
            address_answer_deletes: [],
            custom_question_ids: [],
            custom_question_answers: [],
            answer_bank_ids: [],
            answer_bank_answers: [],
            deleted_answers_custom_question_ids: [],
            new_answer_bank_entries: [],
          },
          undefined,
          {
            awaitRefetchQueries: true,
          }
        )
          .then(async () => {
            // Refetch the form view and verifications queries to update UI
            await client.refetchQueries({
              include: [GET_FORM_VIEW_BY_ID, GET_VERIFICATIONS_FOR_ADMIN],
            });

            // Automatically mark verification as verified when field is updated (unless explicitly skipped)
            if (
              !skipAutoVerification &&
              selectedVerification?.verification?.id
            ) {
              await verificationHandler(
                form.id,
                selectedVerification.verification.id,
                selectedVerification.verification.label,
                GQL.verification_status_enum.Verified
              );
            }
          })
          .catch((error) => {
            console.error("Error updating address answer:", error);
            avelaToast.error({
              title: `Failed to update address`,
              description: "Please try again or contact support.",
            });
          });

        return;
      }

      let answerObject = {};

      switch (question.type) {
        case "Date":
          answerObject = { date_answer: value || null };
          break;
        case "FreeText":
        case "Email":
        case "PhoneNumber":
        case "Grades":
          answerObject = { free_text_answer: value };
          break;
        default:
          return;
      }

      // Use the hook to update the form answer in the database
      upsertFormAnswers(
        {
          form_id: form.id,
          single_or_multi_select_question_ids: [],
          answers: [
            {
              form_id: form.id,
              question_id: questionId,
              ...answerObject,
            },
          ],
          grades_answers: [],
          address_answers: [],
          address_answer_deletes: [],
          custom_question_ids: [],
          custom_question_answers: [],
          answer_bank_ids: [],
          answer_bank_answers: [],
          deleted_answers_custom_question_ids: [],
          new_answer_bank_entries: [],
        },
        undefined,
        {
          awaitRefetchQueries: true,
        }
      )
        .then(async () => {
          // Refetch the form view and verifications queries to update UI
          await client.refetchQueries({
            include: [GET_FORM_VIEW_BY_ID, GET_VERIFICATIONS_FOR_ADMIN],
          });

          // Automatically mark verification as verified when field is updated (unless explicitly skipped)
          if (!skipAutoVerification && selectedVerification?.verification?.id) {
            await verificationHandler(
              form.id,
              selectedVerification.verification.id,
              selectedVerification.verification.label,
              GQL.verification_status_enum.Verified
            );
          }
        })
        .catch((error) => {
          console.error("Error updating answer:", error);
          avelaToast.error({
            title: `Failed to update answer`,
            description: "Please try again or contact support.",
          });
        });
    },
    [
      selectedVerification,
      form.id,
      upsertFormAnswers,
      client,
      verificationHandler,
      avelaToast,
    ]
  );

  // Handle verification view click
  const handleVerificationView = useCallback(
    async (
      verification: AF.FormVerification<AF.WithId>,
      questions: AF.Question<AF.WithId>[],
      answers: Record<string, any>
    ) => {
      const files = verificationModalFiles;

      const verificationDocIds = new Set(
        questions
          .filter((q) => q.type === AF.FileUploadType)
          .flatMap((q) => {
            const answer = answers[q.id];
            return Array.isArray(answer) ? answer : [];
          })
      );

      const openVerificationModal = (
        resolvedFiles: VerificationModalFile[]
      ) => {
        const firstIdx = resolvedFiles.findIndex((f) =>
          verificationDocIds.has(f.id)
        );
        setInitialFileIndex(firstIdx >= 0 ? firstIdx : 0);
        setSelectedVerification({
          verification,
          questions,
          answers,
          files: resolvedFiles,
        });
        const verificationResult = form.form_verification_results.find(
          (result) => result.form_verification.id === verification.id
        );
        setCurrentVerificationStatus(
          verificationResult?.verification_status ||
            GQL.verification_status_enum.Pending
        );
        onOpen();
      };

      try {
        if (files.length === 0) {
          avelaToast({
            title: "No documents to display",
            description:
              "There are no uploaded documents to show for this form.",
            status: "warning",
          });
          openVerificationModal([]);
          return;
        }

        // Fetch metadata and URLs for all documents
        const filePromises = files.map(async (file) => {
          const documentId = file.id;
          try {
            // Get document metadata
            const info = await getDocumentInfo(documentId);
            // Get file download URL
            const url = await getDownloadUrl(documentId);

            return {
              id: documentId,
              name: info?.filename || `File ${documentId.substring(0, 5)}`,
              url: url?.status === DocumentStorageStatus.OK ? url.data : "",
              identityAiEligible: file.identityAiEligible,
            };
          } catch (err) {
            console.error(`Error fetching document ${documentId}:`, err);
            return {
              id: documentId,
              name: `File ${documentId.substring(0, 8)}`,
              url: "",
              identityAiEligible: file.identityAiEligible,
            };
          }
        });

        // Wait for all promises to resolve
        const fileData = await Promise.all(filePromises);
        openVerificationModal(fileData);
      } catch (error) {
        console.error("Error fetching document data:", error);
        avelaToast.error({
          title: "Could not load documents",
          description:
            "Please try again. If the problem continues, contact support.",
        });
        openVerificationModal([]);
      }
    },
    [
      avelaToast,
      form.form_verification_results,
      getDocumentInfo,
      getDownloadUrl,
      onOpen,
      setSelectedVerification,
      setCurrentVerificationStatus,
      verificationModalFiles,
    ]
  );

  return (
    <VStack gap={2} align="stretch">
      {confirmationDialog}

      <VerificationModal
        isOpen={isOpen}
        onClose={(options) => {
          // Handle the toast when closing with save
          if (options?.showToast && selectedVerification) {
            const toastType = options.toastType || "success";
            avelaToast({
              title: `${selectedVerification.verification.label} verification saved`,
              status: toastType,
              duration: 3000,
            });
          }
          onClose();
        }}
        selectedVerification={selectedVerification}
        initialFileIndex={initialFileIndex}
        form={form}
        currentVerificationStatus={currentVerificationStatus}
        onVerificationStatusChange={(status) => {
          setCurrentVerificationStatus(status);
          if (selectedVerification?.verification?.id) {
            verificationHandler(
              form.id,
              selectedVerification.verification.id,
              selectedVerification.verification.label,
              status
            );
          }
        }}
        canEditVerificationData={canEditVerificationData}
        onAnswerUpdate={handleAnswerUpdate}
      />

      {questionsGroupedByVerifications.map(
        ({ verification, questions }, index) => {
          // If no verification or parent user, just render questions directly
          if (!verification || isParentUser) {
            return questions.map((question, qidx) => {
              const isHidden = !applicableQuestionIdSet.has(question.id);
              return (
                <Box key={qidx} display="flex">
                  <AdminQuestionAnswer
                    marginBottom={4}
                    question={question}
                    answer={answers[question.id]}
                    formId={form.id}
                  />
                  {isHidden ? (
                    <Tooltip label="Question is hidden">
                      <Box>
                        <Icon as={RiEyeOffLine} color="blackAlpha.500" />
                      </Box>
                    </Tooltip>
                  ) : null}
                </Box>
              );
            });
          }

          // Otherwise render a verification section
          return (
            <Box id={`verification-${verification.id}`} key={index}>
              <VerificationSection
                verification={verification}
                questions={questions}
                applicableQuestionIdSet={applicableQuestionIdSet}
                answers={answers}
                form={form}
                onVerificationClick={() =>
                  handleVerificationView(verification, questions, answers)
                }
                verificationHandler={verificationHandler}
                isVerificationModalEnabled={isVerificationModalEnabled}
                hasFileUploads={
                  verificationsWithUploads[verification.id] || false
                }
                isSubmitting={isSubmittingVerification}
              />
            </Box>
          );
        }
      )}
    </VStack>
  );
};
